import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useEditMode } from '../EditModeContext';
import { Save, X, AlertCircle, Eye } from 'lucide-react';

const EditToolbar = () => {
  const navigate = useNavigate();
  const {
    isEditMode,
    hasUnsavedChanges,
    saveChanges,
    disableEditMode,
    discardChanges
  } = useEditMode();

  if (!isEditMode) return null;

  const handleSave = async () => {
    const success = await saveChanges();
    if (success) {
      alert('Changes saved successfully!');
    } else {
      alert('Failed to save changes. Please try again.');
    }
  };

  const handleExit = async () => {
    const canExit = await disableEditMode();
    if (canExit) {
      navigate('/dashboard');
    }
  };

  const handleDiscard = () => {
    const discarded = discardChanges();
    if (discarded) {
      alert('Changes discarded.');
    }
  };

  const handlePreview = () => {
    // Temporarily disable edit mode to show preview
    const currentUrl = window.location.pathname;
    window.open(currentUrl, '_blank');
  };

  return (
    <div className="fixed top-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg z-[9998]">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Left side - Status */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span className="font-semibold">Edit Mode</span>
            </div>

            {hasUnsavedChanges && (
              <div className="flex items-center gap-2 text-yellow-200">
                <AlertCircle size={16} />
                <span className="text-sm">Unsaved changes</span>
              </div>
            )}
          </div>

          {/* Center - Instructions */}
          <div className="hidden md:block text-sm text-blue-100">
            Click on text or images to edit • Press Enter to save • ESC to cancel
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handlePreview}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-md transition-colors text-sm"
              title="Preview in new tab"
            >
              <Eye size={16} />
              <span className="hidden sm:inline">Preview</span>
            </button>

            {hasUnsavedChanges && (
              <button
                onClick={handleDiscard}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-md transition-colors text-sm"
              >
                <X size={16} />
                <span className="hidden sm:inline">Discard</span>
              </button>
            )}

            <button
              onClick={handleSave}
              disabled={!hasUnsavedChanges}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors text-sm font-medium ${
                hasUnsavedChanges
                  ? 'bg-green-500 hover:bg-green-600 text-white'
                  : 'bg-white/10 text-white/50 cursor-not-allowed'
              }`}
            >
              <Save size={16} />
              <span className="hidden sm:inline">Save Changes</span>
            </button>

            <button
              onClick={handleExit}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-md transition-colors text-sm ml-2"
            >
              <X size={16} />
              <span className="hidden sm:inline">Exit</span>
            </button>
          </div>
        </div>

        {/* Mobile instructions */}
        <div className="md:hidden mt-2 text-xs text-blue-100 text-center">
          Tap text or images to edit
        </div>
      </div>
    </div>
  );
};

export default EditToolbar;
